/**
 * My Entities Page
 *
 * Borrower-facing list of borrowing entities tied to the consumer's loans.
 * Pure list page (entities only — the legacy combined entities-and-guarantees
 * view moved to its own /guarantees page per the Fiops Borrower Portal
 * redesign, reference-section.md).
 *
 * Layout: PageHeader with "Add entity" CTA + 4-tile KPI strip (Active
 * entities / Active loans / States / Need attention) + flat row list using
 * `EntityRow`. Row click navigates to `/entities/:id` (EntityHubPage).
 *
 * @see Issue #636 - Multi-Entity Borrower Relationships
 * @see Issue #809 - Consumer Entity Creation & Management
 */

import EntityIcon from '@mui/icons-material/AccountBalance';
import AppsIcon from '@mui/icons-material/Apps';
import FlagIcon from '@mui/icons-material/Flag';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, EmptyState, GlassCard, ShimmerLoader, Stack, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityRow, type EntityRowData } from '../../components/entities/EntityRow';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { LoanKpiTile } from '../../components/loans/LoanKpiTile';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { useMyBorrowingEntities } from '../../lib/trpc/hooks';

// ============================================================================
// Types — list-row projection of the wire DTO
// ============================================================================

interface EntityFromApi {
  id: string;
  legalName: string;
  entityType: string;
  ein?: string | null;
  status: string;
  ownerCount?: number;
  activeLoanCount?: number;
  stateOfFormation?: string | null;
  formationDate?: string | null;
  // Some surfaces return owners[] for hover/expansion — we read names lazily
  owners?: Array<{ ownerName: string | null }>;
}

// ============================================================================
// Component
// ============================================================================

export default function MyEntitiesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { consumer } = useConsumerAuth();

  const { data, isLoading } = useMyBorrowingEntities(
    consumer?.id ? { page: 1, limit: 50, consumerId: consumer.id } : undefined,
  );

  const entities = useMemo<EntityRowData[]>(() => {
    const rows = ((data?.data ?? []) as EntityFromApi[]).map((e) => ({
      id: e.id,
      legalName: e.legalName,
      entityType: e.entityType,
      ein: e.ein,
      stateOfFormation: e.stateOfFormation,
      formationDate: e.formationDate,
      // Owners come back from the list endpoint as a relation count, not a
      // names array — surface a placeholder until we wire the names through.
      // TODO: extend `loanEntities.list` to project the first 2-3 owner
      // names so this column isn't empty for entities with owners.
      ownerNames: (e.owners ?? [])
        .map((o) => o.ownerName)
        .filter((n): n is string => Boolean(n))
        .slice(0, 3),
      activeLoanCount: e.activeLoanCount ?? 0,
      status: e.status,
    }));
    return rows;
  }, [data?.data]);

  // KPI derivations
  const activeEntities = entities.length;
  const activeLoans = entities.reduce((sum, e) => sum + e.activeLoanCount, 0);
  const distinctStates = new Set(entities.map((e) => e.stateOfFormation).filter((s): s is string => Boolean(s)));
  // TODO(spec triage flag #1): "Need attention" definition lives at the platform
  // layer — for now flag entities missing an EIN (the most common gap). Once
  // backend surfaces a structured `hasMissingEinDoc` / `hasExpiringFiling`
  // signal, replace this with the real predicate.
  const needAttention = entities.filter((e) => !e.ein).length;

  const handleRowClick = (entityId: string) => {
    navigate(`/entities/${entityId}`);
  };

  // NOTE: The "Add entity" CTA was removed because consumer-side entity
  // creation is not yet implemented end-to-end. The button used to navigate
  // to `/entities/new`, but no matching route exists — clicks fell through
  // to `entities/:entityId` with `entityId='new'`, which renders the
  // not-found error state instead of a creation form. Backend support
  // exists at `loanEntities.create` but it's gated by `businessUserProcedure`
  // (rejects consumer role). When consumer entity creation is built, restore
  // the GlassButton with a real form (inline SplitPanel pattern is preferred
  // over a separate /entities/new page). See Issue #809 / Issue #636.
  return (
    <PageTemplate
      variant="list"
      titleKey="consumer.entities.pageTitle"
      descriptionKey="consumer.entities.pageSubtitle"
      icon={EntityIcon}
    >
      {/* KPI strip — 4 tiles */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <LoanKpiTile
          label={t('consumer.entities.kpiActiveEntities')}
          value={activeEntities}
          tone="mint"
          icon={EntityIcon}
          loading={isLoading}
        />
        <LoanKpiTile
          label={t('consumer.entities.kpiActiveLoans')}
          value={activeLoans}
          tone="id"
          icon={AppsIcon}
          loading={isLoading}
        />
        <LoanKpiTile
          label={t('consumer.entities.kpiStates')}
          value={distinctStates.size}
          sub={Array.from(distinctStates).slice(0, 4).join(' · ')}
          tone="orange"
          icon={FlagIcon}
          loading={isLoading}
        />
        <LoanKpiTile
          label={t('consumer.entities.kpiNeedAttention')}
          value={needAttention}
          tone="amber"
          icon={WarningAmberIcon}
          loading={isLoading}
        />
      </Box>

      {/* Entity list */}
      <GlassCard sx={{ p: 0 }}>
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            <ShimmerLoader variant="rectangular" height={240} />
          </Box>
        ) : entities.length === 0 ? (
          <EmptyState
            icon={<EntityIcon />}
            title={t('consumer.entities.sectionTitle')}
            description={t('borrowingEntities.emptyDescription')}
          />
        ) : (
          <>
            {/* Sticky column header row — matches EntityRow's 7-col grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.8fr 1fr 1fr 1.8fr 100px 90px 36px',
                gap: 1.5,
                px: 1.5,
                py: 1,
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
                borderTopLeftRadius: 2,
                borderTopRightRadius: 2,
              }}
            >
              <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
                {t('consumer.entities.col.legalName', { defaultValue: 'Legal name' })}
              </Typography>
              <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
                {t('consumer.entities.col.type', { defaultValue: 'Type' })}
              </Typography>
              <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
                {t('consumer.entities.col.einState', { defaultValue: 'EIN · State' })}
              </Typography>
              <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
                {t('consumer.entities.col.owners', { defaultValue: 'Owners' })}
              </Typography>
              <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
                {t('consumer.entities.col.activeLoans', { defaultValue: 'Loans' })}
              </Typography>
              <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
                {t('consumer.entities.col.status', { defaultValue: 'Status' })}
              </Typography>
              <Box />
            </Box>
            <Stack divider={<Box sx={{ height: 1, bgcolor: 'divider', mx: 1.5 }} />}>
              {entities.map((entity) => (
                <EntityRow key={entity.id} entity={entity} onClick={handleRowClick} />
              ))}
            </Stack>
          </>
        )}
      </GlassCard>
    </PageTemplate>
  );
}
